import { formatDate, formatMoney, useLocale, useTranslation } from '@multaqa/i18n'
import { SummaryStrip } from '@multaqa/ui/components/ui/summary-strip'
import { InlineQueryError } from '@/components/inline-query-error'
import { useCustomerFinancialSummary } from '../hooks/use-customers'

export function CustomerFinancialSummaryStrip({ customerId }: { customerId: string }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const summary = useCustomerFinancialSummary(customerId)

  if (summary.isError) {
    return (
      <InlineQueryError
        title={t('common:error.asyncTitle')}
        description={t('customer.financialSummary.loadError')}
        retryLabel={t('common:action.retry')}
        onRetry={() => void summary.refetch()}
      />
    )
  }

  const amount = (value: string | null | undefined) => {
    if (value === null || value === undefined || !summary.data?.currency) return '—'
    return formatMoney(value, summary.data.currency, locale)
  }
  const metrics = [
    { label: t('customer.financialSummary.balance'), value: amount(summary.data?.balance) },
    { label: t('customer.financialSummary.overdue'), value: amount(summary.data?.overdue_amount) },
    {
      label: t('customer.financialSummary.unapplied'),
      value: amount(summary.data?.unapplied_amount),
    },
    {
      label: t('customer.financialSummary.lastPayment'),
      value: summary.data?.last_payment_date
        ? formatDate(new Date(summary.data.last_payment_date), locale)
        : '—',
    },
  ]

  return (
    <SummaryStrip
      role="group"
      aria-label={t('customer.financialSummary.title')}
      data-testid="customer-financial-summary"
      items={metrics}
      loading={summary.isLoading}
    />
  )
}
